"use client"

import type { CSSProperties, ReactNode } from "react"
import { usePathname } from "next/navigation"
import { analytics } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import {
  contentSlugFromPathname,
  pageFamilyFromPathname,
} from "@/lib/analytics/content-links"

/**
 * Container impression for a content module: `cta_surface_shown` with
 * `cta_location: content_<module>`, so the module's `content_link_clicked`
 * rows finally have a denominator.
 */
export function useContentModuleImpression<T extends HTMLElement>(
  module: string,
  count?: number,
) {
  const pathname = usePathname()
  const pageFamily = pageFamilyFromPathname(pathname)
  return useImpressionEvent<T>(
    `content_${module}`,
    {
      surface: module,
      count,
      slug: contentSlugFromPathname(pathname),
      page_family: pageFamily,
    },
    { emit: analytics.ctaSurfaceShown, enabled: pageFamily !== null },
  )
}

interface ContentModuleImpressionProps {
  module: string
  /** Links (or cards) inside the module, so CTR can be read per link. */
  count?: number
  className?: string
  style?: CSSProperties
  id?: string
  children: ReactNode
}

/**
 * Wrapper for server-rendered modules: carries the `data-module` attribute the
 * delegated click listener resolves and fires the container impression.
 */
export function ContentModuleImpression({
  module,
  count,
  className,
  style,
  id,
  children,
}: ContentModuleImpressionProps) {
  const ref = useContentModuleImpression<HTMLDivElement>(module, count)
  return (
    <div ref={ref} data-module={module} className={className} style={style} id={id}>
      {children}
    </div>
  )
}
